import type { FileEntryCondition, WorkflowStep } from '@/api/workflows';

/**
 * Human-readable summaries of visibility conditions, shared by the read-only
 * review surfaces (WorkflowReviewBody's condition peek + StepConfigDrawer's
 * per-field / per-section condition display). Extracted so there's a single
 * summariser — field labels resolve off the workflow's step list so a rule
 * reads `<authored label> equals "yes"` instead of a raw guid.
 */

/** Operator → human phrasing. `?? c.operator` fallback in describeCondition
 *  handles anything new the BE ships before this table gets updated. */
export const OPERATOR_TEXT: Record<string, string> = {
  equals: 'equals',
  not_equals: 'is not',
  is_true: 'is true',
  is_false: 'is false',
  contains: 'contains',
  not_contains: 'does not contain',
  age_years_lt: 'age <',
  age_years_ge: 'age ≥',
  count_eq: 'instance count =',
  count_ge: 'instance count ≥',
};

/** Walk every step's field tree looking for a matching id. Also accepts a
 *  section id (count operators point at sections not fields — the sectionId
 *  case surfaces the section title). Returns a friendly fallback when nothing
 *  matches. */
export function resolveFieldLabel(id: string, steps: WorkflowStep[], isFr: boolean): string {
  for (const step of steps ?? []) {
    for (const section of step.sections ?? []) {
      if (section.id === id) {
        return (isFr ? section.titleFr : section.titleEn) || section.titleEn || 'section';
      }
      for (const row of section.rows ?? []) {
        for (const col of row.columns ?? []) {
          if (col.field?.id === id) {
            return (isFr ? col.field.labelFr : col.field.labelEn) || col.field.labelEn || 'field';
          }
        }
      }
    }
  }
  return 'field';
}

/**
 * Render a single condition as `<field label> <operator> <value>`. Falls back
 * to the raw operator + value when the field id can't be resolved.
 */
export function describeCondition(
  c: FileEntryCondition,
  steps: WorkflowStep[],
  isFr: boolean,
): string {
  const fieldLabel = resolveFieldLabel(c.fieldId, steps, isFr);
  const opText = OPERATOR_TEXT[c.operator] ?? c.operator;
  const valueText = c.value ? `"${c.value}"` : '';
  // A quantified source lives inside a repeatable section, so the rule reads
  // over instances rather than over one answer — say which, or "Beneficiary
  // Province equals BC" hides whether one or all beneficiaries must match.
  const quantifier = c.instanceScope === 'all' ? 'every ' : c.instanceScope === 'any' ? 'any ' : '';
  return `${quantifier}${fieldLabel} ${opText} ${valueText}`.trim();
}

export type ConditionSummaryEntry = { scope: string; summary: string };

/**
 * Walk a step's own conditions + every section inside it, and summarise each
 * rule as `{ scope, summary }` — the condition-peek Popover renders these
 * bullet-by-bullet. Scope names WHY the rule is gating this step ("Step gate"
 * vs the specific section title); summary spells the rule out with the source
 * field's authored label.
 */
export function collectStepConditions(
  step: WorkflowStep,
  steps: WorkflowStep[],
  isFr: boolean,
): ConditionSummaryEntry[] {
  const out: ConditionSummaryEntry[] = [];
  const stepScope = 'Step gate';
  for (const c of step.conditions ?? []) {
    out.push({ scope: stepScope, summary: describeCondition(c, steps, isFr) });
  }
  for (const section of step.sections ?? []) {
    const sectionTitle =
      (isFr ? section.titleFr : section.titleEn) || section.titleEn || section.titleFr || 'Section';
    for (const c of section.conditions ?? []) {
      out.push({
        scope: `Section «${sectionTitle}»`,
        summary: describeCondition(c, steps, isFr),
      });
    }
  }
  return out;
}
